import { useState } from "react";
import { useAuthContext } from "../context-state/AuthContext";
import { useDashboardContext } from "../context-state/DashboardContext";
import { EventService } from "../services/eventService";
import { hasRecurringTimeSlots } from "../utils/timeSlots";

/**
 * Fetches the whole dashboard data-set of an event (orders, userEvents, tickets,
 * coupons, utms, links) and writes it into DashboardContext.
 *
 * Extracted out of EventGuard so the same fetch can be triggered on demand: the
 * orders tab re-uses it for its Refresh button instead of refetching on every
 * navigation into the tab. On a big event that payload is several MB, so it must
 * only be paid when something actually needs it.
 */
export const useEventDashboardData = () => {
    const { account } = useAuthContext();
    const {
        userEventContext,
        eventOrderContext,
        ticketContext,
        couponContext,
        utmContext,
        linkContext,
    } = useDashboardContext();

    const { updateManyEventOrders, updateEventToEventOrdersRelation } = eventOrderContext;
    const { updateManyUserEvents, updateEventToUserEventRelation } = userEventContext;
    const { updateManyCoupons, updateEventToCouponsRelation } = couponContext;
    const { updateManyTickets, updateEventToTicketsRelation } = ticketContext;
    const { updateManyUtms, updateEventToUtmsRelation } = utmContext;
    const { updateManyLinks, updateEventToLinksRelation } = linkContext;

    const [isLoading, setIsLoading] = useState(false);

    const fetchEventDashboardData = async (event, { includeOrders = true, includeExtras = true, silent = false } = {}) => {
        if (!event) return false;
        try {
            if (!silent) setIsLoading(true);
            const {
                userEvents,
                eventOrders,
                tickets,
                coupons,
                utms,
                links,
            } = await EventService.getEventDashboardData({
                accountId: account._id,
                eventId: event._id,
                isTimeSlot: hasRecurringTimeSlots(event),
                includeOrders,
                includeExtras,
            });

            // Tickets / coupons / utms / links — skipped entirely when already held.
            if (includeExtras) {
                const normalizedTickets = tickets || [];
                updateManyTickets(normalizedTickets);
                updateEventToTicketsRelation(event._id, normalizedTickets);
            }

            // UserEvent + EventOrders.
            // In light mode these were never requested, so we must leave the relations
            // untouched — writing an empty array here would read as "loaded, zero orders"
            // downstream (an empty array is truthy) and hide real orders.
            if (includeOrders) {
                const normalizedUserEvents = userEvents || [];
                updateManyUserEvents(normalizedUserEvents);
                updateEventToUserEventRelation(event._id, normalizedUserEvents);

                const normalizedEventOrders = eventOrders || [];
                updateManyEventOrders(normalizedEventOrders);
                updateEventToEventOrdersRelation(event._id, normalizedEventOrders);
            }

            if (includeExtras) {
                const normalizedCoupons = coupons || [];
                updateManyCoupons(normalizedCoupons);
                updateEventToCouponsRelation(event._id, normalizedCoupons);

                const normalizedUtms = utms || [];
                updateManyUtms(normalizedUtms);
                updateEventToUtmsRelation(event._id, normalizedUtms);

                const normalizedLinks = links || [];
                updateManyLinks(normalizedLinks);
                updateEventToLinksRelation(event._id, normalizedLinks);
            }

            return true;
        } catch (e) {
            console.error(e);
            return false;
        } finally {
            if (!silent) setIsLoading(false);
        }
    };

    return { isLoading, fetchEventDashboardData };
};
